import {
  THUE_MY_MAC_DINH_PCT,
  hopDongPhuNgay,
  laLinkRacTheoNgay,
  type PartnerChannelRow,
  type PartnerContract,
} from '@qlk/shared';
import { useState } from 'react';

import { HopXacNhan, Nut, O, OChonTruong, homNayVn, ngayDay } from '../../../ui/index.js';
import { useSuaLink, useXoaLink } from '../api.js';

/**
 * Nút "Sửa ngày hiệu lực / tỷ lệ" của một dòng link, kèm nút xoá dòng rác.
 *
 * Dòng rác là dòng `joinedAt = leftAt`: sinh ra khi gán kênh vào cùng Network
 * lần nữa (service đóng link cũ đúng ngày bắt đầu link mới). Dòng có khoảng
 * thật là lịch sử chia tiền nên không cho xoá — chỉ sửa ngày.
 */

const ngayCuaIso = (iso: string): string => iso.slice(0, 10);

export function HangSuaLink({
  dong,
  hopDong = [],
  adsense = false,
}: {
  dong: PartnerChannelRow;
  /** Hợp đồng đang sống của đối tác, để chọn trong ô "Hợp đồng". */
  hopDong?: PartnerContract[];
  /** Tài khoản AdSense: không có tỷ lệ riêng nên ẩn ô đó. */
  adsense?: boolean;
}) {
  const [mo, datMo] = useState(false);
  const [hoiXoa, datHoiXoa] = useState(false);
  /**
   * Lỗi gần nhất của lượt xoá, giữ **ngoài hộp thoại**.
   *
   * Hộp thoại chỉ sống khi `hoiXoa` còn `true`; bảng refetch (TanStack tải lại
   * khi cửa sổ được focus) làm component remount và lỗi biến mất không dấu vết.
   */
  const [loiXoa, datLoiXoa] = useState<string | null>(null);

  const [tuNgay, datTuNgay] = useState(ngayCuaIso(dong.joinedAt));
  const [denNgay, datDenNgay] = useState(dong.leftAt === null ? '' : ngayCuaIso(dong.leftAt));
  const [tyLe, datTyLe] = useState(dong.sharePercent ?? '');
  const [hopDongChon, datHopDongChon] = useState(dong.contractId ?? '');

  const sua = useSuaLink();
  const xoa = useXoaLink();

  /*
   * Danh sách chọn = hợp đồng phủ ngày vào đang gõ, **cộng** bản đang gắn nếu
   * nó không phủ ngày đó. Thiếu bản đang gắn thì `<select>` rơi về mục đầu
   * ("Chưa gắn hợp đồng") trong khi state vẫn giữ id cũ: người dùng thấy một
   * thứ mà bấm Lưu lại gửi thứ khác.
   */
  const phuNgayVao = hopDongPhuNgay(hopDong, tuNgay);
  const dangGan = hopDong.find((c) => c.id === hopDongChon);
  const luaChonHopDong = [
    { value: '', label: 'Chưa gắn hợp đồng' },
    ...phuNgayVao.map((c) => ({
      value: c.id,
      label: `${c.contractNo} · hiệu lực ${ngayDay(c.startDate)}`,
    })),
    ...(dangGan && !phuNgayVao.some((c) => c.id === dangGan.id)
      ? [
          {
            value: dangGan.id,
            label: `${dangGan.contractNo} · ngoài khoảng ngày vào`,
          },
        ]
      : []),
  ];

  // Cùng luật với API (`laLinkRac` của module networks).
  const laRac = laLinkRacTheoNgay(dong);

  if (!mo) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Nut
          kieu="phu"
          onClick={() => {
            datMo(true);
          }}
        >
          Sửa ngày hiệu lực / tỷ lệ
        </Nut>

        {dong.contractNo === null && (
          <span className="text-phu text-warn-chu">Chưa gắn hợp đồng</span>
        )}

        {laRac && (
          <>
            <Nut
              kieu="phu"
              onClick={() => {
                datHoiXoa(true);
              }}
            >
              Xoá dòng rác
            </Nut>

            {hoiXoa && (
              <HopXacNhan
                title="Xoá dòng liên kết rác"
                nhanXacNhan="Xoá dòng"
                nguyHiem
                dangChay={xoa.isPending}
                loi={xoa.error?.message ?? null}
                onDong={() => {
                  datHoiXoa(false);
                }}
                onXacNhan={() => {
                  datLoiXoa(null);
                  xoa.mutate(dong.linkId, {
                    onSuccess: () => {
                      datHoiXoa(false);
                    },
                    onError: (e) => {
                      datLoiXoa(e.message);
                    },
                  });
                }}
              >
                <p>
                  {dong.channelName} · {dong.partnerName}: ngày vào và ngày rời đều là{' '}
                  {ngayDay(dong.joinedAt)}, nên dòng này không chia tiền kỳ nào. Lịch sử thật của
                  kênh vẫn còn ở các dòng khác.
                </p>
              </HopXacNhan>
            )}
          </>
        )}

        {loiXoa !== null && (
          <p role="alert" className="w-full text-right text-phu text-bad-chu">
            Không xoá được: {loiXoa}
          </p>
        )}
      </div>
    );
  }

  return (
    <form
      className="flex flex-wrap items-end justify-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        sua.mutate(
          {
            linkId: dong.linkId,
            joinedAt: `${tuNgay}T00:00:00.000Z`,
            leftAt: denNgay === '' ? null : `${denNgay}T00:00:00.000Z`,
            sharePercent: adsense || tyLe === '' ? null : Number(tyLe),
            /*
             * `null` là **tách** link khỏi hợp đồng — khác hẳn lúc tạo link
             * (ở đó `null` nghĩa là "để API tự chọn"). Sửa là lúc người dùng
             * nói rõ ý mình, nên gửi đúng thứ họ chọn.
             */
            contractId: hopDongChon === '' ? null : hopDongChon,
          },
          {
            onSuccess: () => {
              datMo(false);
            },
          },
        );
      }}
    >
      <O
        label="Từ ngày"
        type="date"
        value={tuNgay}
        max={homNayVn()}
        onChange={datTuNgay}
        className="w-[160px]"
      />
      <O
        label="Đến ngày"
        type="date"
        value={denNgay}
        max={homNayVn()}
        onChange={datDenNgay}
        moTa="Bỏ trống = đang hiệu lực."
        className="w-[160px]"
      />
      {/*
       * Ô "Hợp đồng": chỉ liệt kê hợp đồng của **chính đối tác này** phủ ngày
       * vào đang chọn — API kiểm lại đúng luật đó (`kiemNgayVaoTheoHopDong`),
       * nên đưa ra bản không phủ ngày vào chỉ để người dùng bấm rồi nhận lỗi.
       */}
      <OChonTruong
        label={adsense ? 'Cài đặt AdSense' : 'Hợp đồng'}
        value={hopDongChon}
        onChange={datHopDongChon}
        options={luaChonHopDong}
        moTa={
          hopDongChon === ''
            ? `Không gắn thì tỷ lệ Network tính 0% và thuế Mỹ lấy mặc định ${String(THUE_MY_MAC_DINH_PCT)}%.`
            : 'Chỉ hiện hợp đồng phủ ngày vào ở ô bên trái.'
        }
        className="min-w-[260px]"
      />

      {!adsense && (
        <O
          label="Tỷ lệ Network giữ (%)"
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={tyLe}
          onChange={datTyLe}
          moTa="Bỏ trống thì dùng tỷ lệ hợp đồng."
          className="w-[180px]"
        />
      )}

      <Nut kieu="chinh" type="submit" dangChay={sua.isPending}>
        Lưu
      </Nut>
      <Nut
        kieu="phu"
        onClick={() => {
          datMo(false);
          sua.reset();
        }}
      >
        Huỷ
      </Nut>

      {sua.error && <p className="w-full text-right text-phu text-bad-chu">{sua.error.message}</p>}
    </form>
  );
}
